import { Component, Input, OnInit } from '@angular/core';
import { AuthenService } from '@core/authen/authen.service';
import { AppendComponentToBodyService } from '@core/services/append-component-to-body.service';
import { CentralConfigService } from '@core/services/central-config.service';
import { SessionService } from '@core/session.service';
import { environment } from '@env/environment';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { ToastsContainerComponent as ToastsComp } from '@shared/components/toasts-container/toasts-container.component';
import { TemplateName } from '@shared/constants/visibility-config.const';
import { StorageEnum } from '@shared/enums/storage.enum';
import { UrlParam } from '@shared/enums/url-param.enum';
import { UserInterface } from '@shared/interfaces/user.interface';
import { FormUtils } from '@shared/utils/form-utils';
import { StringUtils } from '@shared/utils/string-utils';
import { Event as EventBusEvent } from 'ngx-eventbus/lib/service/event';
import { filter, switchMap, take, timeout } from 'rxjs/operators';

const API_READY_TIMEOUT_MS = 15000;

@UntilDestroy()
@Component({
  selector: 'app-bootstrap',
  template: ``,
  styles: ['{ display: none; }'],
  standalone: true,
})
export class BootstrapComponent implements OnInit {
  @Input() set domain(value: string) {
    if (!value) return;
    this.sessionService.setDomain(value);
  }

  @Input() set isId(value: string) {
    if (!value) return;
    this.setInnovationSpace(value);
  }

  @Input() set uiTemplate(value: TemplateName) {
    this.centralConfigService.uiTemplateConfig$.next(value);
  }

  @Input() set apiUrl(value: string) {
    if (!value) return;
    this.centralConfigService.apiUrl$.next(value);
  }

  @Input() set newsRedirectPath(value: string) {
    if (!value) return;
    this.centralConfigService.newsRedirectPath = value;
  }

  openEnforceProfileDialogEvent: EventBusEvent;

  constructor(
    public readonly sessionService: SessionService,
    private readonly centralConfigService: CentralConfigService,
    private readonly appendService: AppendComponentToBodyService,
    private readonly authService: AuthenService,
    private readonly modalService: NgbModal
  ) {}

  ngOnInit(): void {
    this.registerToastComp();
    this.redirectToNewsPage();
    this.handleTokenExpire();

    this.authService.openEnforceProfileDialog$
      .pipe(untilDestroyed(this))
      .subscribe((id: number) => {
        void this.openEnforceProfileDialog(id);
      });
  }

  setInnovationSpace(id: string): void {
    this.centralConfigService.innovationSpaceId = Number(id);
    this.centralConfigService.read(id).subscribe();
  }

  registerToastComp(): void {
    const isRendered = document.getElementsByTagName('app-toasts');

    if (isRendered?.length) return;

    this.appendService.append(ToastsComp);
  }

  handleTokenExpire(): void {
    const tokenExpireTime = this.sessionService.getCookie(
      StorageEnum.expiredTokenTime
    );

    if (!tokenExpireTime || !this.sessionService.isTokenExpired()) {
      this.authService.finishSessionRestore();

      return;
    }

    if (this.authService.getRefreshToken()) {
      this.sessionService.apiReady$
        .pipe(
          filter(Boolean),
          take(1),
          timeout(API_READY_TIMEOUT_MS),
          switchMap(() => this.authService.refreshToken()),
          untilDestroyed(this)
        )
        .subscribe({
          next: (data: UserInterface) => {
            if (data?.accessToken) {
              this.authService.handleLoginSuccess(data);
              this.authService.updateProfileInfo(true);

              return;
            }

            this.expireSession();
          },
          error: () => this.expireSession(),
        });

      return;
    }

    this.expireSession();
  }

  redirectToNewsPage(): void {
    const tabNewsParam = StringUtils.getParamFromUrl(UrlParam.TabNews);
    const isOnNewsPage = globalThis.location.href.includes(
      this.centralConfigService.newsRedirectPath
    );

    if (!tabNewsParam || isOnNewsPage) return;

    const url = `${this.centralConfigService.newsRedirectPath}/?${UrlParam.TabNews}=message`;
    FormUtils.navigateToWithLanguagePath(this.sessionService, url);
  }

  /* *********************Private************************ */
  private expireSession(): void {
    if (
      !location.href.includes(environment.jipUrl.login) &&
      !location.href.includes(environment.jipUrl.processToken)
    ) {
      this.sessionService.setLoginCallBackpage(location.href);
    }

    this.authService.resetSession();
    this.sessionService.setCookie(StorageEnum.isTokenExpired, true);
  }

  private async openEnforceProfileDialog(id: number): Promise<void> {
    // The profile form is large and rarely needed: load it on demand.
    const { EnforceProfileDialogComponent } = await import(
      '@shared/components/enforce-profile-dialog/enforce-profile-dialog.component'
    );

    const modalRef = this.modalService.open(EnforceProfileDialogComponent, {
      backdrop: 'static',
      size: 'sm',
      modalDialogClass: 'enforce-profile-modal',
      keyboard: false,
    });

    modalRef.componentInstance.id = id;
  }
}
